import React from 'react';
import styled, { useTheme } from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, LabelUppercase } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompanies } from '../../../hooks/use-hydra/admin';
import { FormInput, FormLabel } from '../../Form';
import Button from '../../elements/Button';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { addCompany } from '../../../api/admin';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { CheckboxSetting } from '../../../pages/Admin/Settings';

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  padding-bottom: 72px;
`;
const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export default function AddCompanyModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { getTokenSilently } = useAuth();
  const { refetch: refetchCompanies, isLoading: isLoadingCompanies } =
    useGetCompanies();
  const theme = useTheme();
  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [isKickOffEnabled, setIsKickOffEnabled] = React.useState(true);
  const [isCompanyProjectUploadsEnabled, setIsCompanyProjectUploadsEnabled] =
    React.useState(true);
  const [isRetroSurveyEnabled, setIsRetroSurveyEnabled] = React.useState(true);
  const [isCohortWelcomeEmailEnabled, setIsCohortWelcomeEmailEnabled] =
    React.useState(true);
  const [isOfficeHoursRemindersEnabled, setIsOfficeHoursRemindersEnabled] =
    React.useState(true);
  const [isAssessmentTestEnabled, setIsAssessmentTestEnabled] =
    React.useState(true);
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const canSubmit = name && slug;
  const submitTooltip = !canSubmit ? 'Please fill in all fields' : '';

  const addCompanyMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const response = await addCompany(token, {
        name: name.trim(),
        slug,
        isKickOffEnabled,
        isCompanyProjectUploadsEnabled,
        isRetroSurveyEnabled,
        isCohortWelcomeEmailEnabled,
        isOfficeHoursRemindersEnabled,
        isAssessmentTestEnabled,
        logoUrl: logoUrl?.trim() || null,
      });
      return response;
    },
    onSuccess: async () => {
      toast.success('Company added successfully');
      await refetchCompanies();
      onClose();
    },
    onError: (error: Error | AxiosError) => {
      const errorMessage = error?.message;
      toast.error(`Failed to add company - ${errorMessage}`);
    },
  });

  const onSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSlug(
      value
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^a-z0-9-]/g, '')
    );
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const onAddCompany = () => {
    addCompanyMutation.mutate();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Add New Company</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <Row>
          <Column>
            <FormLabel>Company Name</FormLabel>
            <FormInput
              placeholder="Company Name"
              value={name}
              onChange={onNameChange}
            />
          </Column>
          <Column>
            <FormLabel>Company Slug</FormLabel>
            <FormInput
              placeholder="Company Slug"
              value={slug}
              onChange={onSlugChange}
            />
          </Column>
        </Row>
        <Row>
          <SettingsWrapper>
            <FormLabel>Settings</FormLabel>
            <CheckboxSetting
              label="Enable cohort invite onboarding email sequence"
              value={isCohortWelcomeEmailEnabled}
              onChange={(value) => {
                setIsCohortWelcomeEmailEnabled(value);
              }}
            />
            <CheckboxSetting
              label="Show kick-off onboarding question & 'Next kickoff' banner on the home page."
              value={isKickOffEnabled}
              onChange={(value) => {
                setIsKickOffEnabled(value);
              }}
            />
            <CheckboxSetting
              label="Enable cohort milestones - Check-in #1, Check-in #2, and Project Upload + notification emails"
              value={isCompanyProjectUploadsEnabled}
              onChange={(value) => {
                setIsCompanyProjectUploadsEnabled(value);
              }}
            />
            <CheckboxSetting
              label="Enable cohort milestone - Retro Survey + notification emails"
              value={isRetroSurveyEnabled}
              onChange={(value) => {
                setIsRetroSurveyEnabled(value);
              }}
            />
            <CheckboxSetting
              label="Enable email reminders 4 hours before office hours"
              value={isOfficeHoursRemindersEnabled}
              onChange={(value) => {
                setIsOfficeHoursRemindersEnabled(value);
              }}
            />
            <CheckboxSetting
              label="Enable Assessment Test admin pages"
              value={isAssessmentTestEnabled}
              onChange={(value) => {
                setIsAssessmentTestEnabled(value);
              }}
            />
          </SettingsWrapper>
        </Row>
        <Row>
          <Column>
            <FormLabel>Company Logo URL</FormLabel>
            <FormInput
              placeholder="Company Logo"
              value={logoUrl ?? ''}
              onChange={(e) => {
                setLogoUrl(e.target.value);
              }}
            />
            <LabelUppercase
              style={{ marginTop: 4, color: theme.colors.textPlaceholder }}
            >
              Optional company logo URL
            </LabelUppercase>
          </Column>
        </Row>
        <Row>
          <Button
            isLoading={addCompanyMutation.isLoading || isLoadingCompanies}
            label="Add Company"
            faIcon="fa-solid fa-plus"
            showIconBeforeLabel
            type="secondary"
            isDisabled={!canSubmit || addCompanyMutation.isLoading}
            tooltip={submitTooltip}
            onClick={onAddCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}
